// NPM Dependencies
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Local Dependencies
// import CustomInput from "../../../misc/CustomInput";
import Alert from "../../../misc/Alert";
import Breadcrumbs from "../../../misc/Breadcrumbs";
import PageHeader from "../../../misc/PageHeader";
import LoadingCircle from "../../../misc/Loading";
import DateInput from "./DateInput";
import ProductsDropdown from "./ProductsDropdown";
import UsersCheckboxes from "./UsersCheckboxes";
import { reconcileSelection } from "./selection";

// Context
import CloudFunctions from "../../../../Context";
import Company from "../../../../lib/uma/companies";
import Client from "../../../../lib/uma/clients";
import Access from "../../../../lib/uma/access";
import Product, { getProducts } from "../../../../lib/uma/products";

import RLSDimsSelector, { IDims } from "./RLSDims";
import { setHours } from "date-fns";

interface IAlert {
  msg: string;
  type: string;
  timeout?: string | number | NodeJS.Timeout;
}


function NewCompanySubscriptionPage() {
  const shopifyFunctions = useContext(CloudFunctions);
  const params = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState<Company>();
  const [clients, setClients] = useState<Client[]>();
  const [products, setProducts] = useState<Product[]>();
  const [access, setAccess] = useState<Access[]>([]);
  const [alertState, setAlertState] = useState<IAlert>({ msg: "", type: "" });
  const [expirationDate, setExpirationDate] = useState<Date>();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>();
  const [productSelected, setProductSelected] = useState<Product>();
  const [rlsValues, setRlsValues] = useState<IDims[] | null>();
  const [rlsValuesSelected, setRlsValuesSelected] = useState<IDims[]>();
  const [isSubmitted, setIsSubmitted] = useState(false);

  // setup company and listeners for products, accesses, and users
  useEffect(() => {
    const company = new Company({ docId: params.companyId });
    company.getCompanyFromFirestore().then((doc) => {
      setCompany(company);
    }, (err) => {
      console.log(err);
    });

    const accessUnsub = company.getAccessListener((snap) => {
      setAccess(snap);
    }, (err) => {
      console.log(err);
    });

    const clientUnsub = company.getClientListener((snap) => {
      // Both updates in one event, so React batches them: there is never a render where the
      // users are listed but the selection has not caught up (which would flash every box
      // unticked before settling).
      setClients(snap);
      setSelectedUserIds((previous) => reconcileSelection(previous, snap));
    }, (err) => {
      console.log(err);
    });

    return () => {
      accessUnsub();
      clientUnsub();
    };
  }, [params.companyId]);

  useEffect(() => {
    getProducts().then(
      (products) => setProducts(products),
      (err) => console.log(err)
    )
  }, []);

  useEffect(() => {
    // fetch RLS Dims
    if (productSelected && productSelected.rls) {
      shopifyFunctions.getRlsDimensions({ fpid: productSelected.docId }).then((result: any) => {
        console.log(result);
        if (result.data) {
          setRlsValues(result.data);
          setRlsValuesSelected([]);
        }
      }, (err: Error) => {
        setRlsValues(null);
        console.log(err);
      })
    }
  }, [shopifyFunctions, productSelected]);

  // TODO: this componenet might need to be fixed. the below can potentially
  // trigger state updates from the timeout after the component has unmounted
  const setAlert = (msg: string, alertType: string, time: number) => {
    cancelAlert();
    const timer = setTimeout(() => {
      setAlertState({
        msg: "",
        type: ""
      });
    }, time);
    setAlertState({
      msg: msg,
      type: alertType,
      timeout: timer
    });
  };

  const cancelAlert = () => {
    if (alertState.timeout) {
      clearTimeout(alertState.timeout);
    }
  };

  const selectProduct = (product: Product) => {
    setProductSelected(product);
    setRlsValuesSelected(undefined);
  };

  /**
   * Hand the grant to the backend. Every step used to run here in the browser — the access
   * doc, the log doc, `newCompanyAccess`, the RLS grants and the log status — five writes with
   * no transaction, and a failure halfway through was swallowed and navigated away from. All
   * of it now lives in one backend module behind `grantCompanyAccess`, which the Slack agent
   * will call in-process too, so there is one grant path rather than two that drift
   * (docs/adr/0009).
   *
   * The checks below stay client-side so the employee gets an instant answer; the backend
   * re-runs all of them against live Firestore and is the authority.
   */
  const submit = async () => {
    // data validation checks
    if (!expirationDate) {
      setAlert("Please provide a valid expiration date.", "warning", 5000);
      return;
    } else if (!productSelected) {
      setAlert("Please select a product.", "warning", 5000);
      return;
    } else if (productSelected.rls && !rlsValuesSelected?.find(
      (dim) => dim.rows.length !== 0)
    ) {
      setAlert("This is an RLS product. Please select at least one value for each dimension.", "warning", 5000);
      return;
    }

    // const newDate = new Date(
    //   new Date(expirationDate).setHours(23, 55)
    // );

    if (!company) {
      throw new Error();
    } else if (!products) {
      throw new Error();
    }

    // pull in this companies access & validate that we don't already have
    // access to this product
    const currentAccess = await company.getAccess();

    if (currentAccess.filter((a) => a.productId === productSelected.docId).length > 0) {
      setAlert(
        "This company already has access to that product!",
        "warning",
        5000
      );
      return;
    }

    setIsSubmitted(true);

    // The expiration is normalized HERE, not on the server: `setHours` resolves in the
    // employee's local timezone, and doing it server-side would silently shift every
    // expiration by the UTC offset. The backend stores the instant it is given.
    const transformedExpDate = setHours(expirationDate, 23);

    // The selection is the selection — no fallback to "all clients". It is seeded with every
    // user when the list loads, so touching nothing still grants to everyone; what changed is
    // that unticking someone now removes exactly that person instead of emptying the list.
    const clientIds = selectedUserIds || [];

    try {
      const response = await shopifyFunctions.grantCompanyAccess({
        company_id: company.docId,
        product_id: productSelected.docId,
        expiration_date: transformedExpDate.toISOString(),
        user_ids: clientIds,
        rls: productSelected.rls ? rlsValuesSelected : null,
      });

      const result = ("data" in response ? response.data : {}) as {
        status?: string;
        message?: string;
        rolled_back?: boolean;
      };

      if (result.status === "error") {
        // Never claim nothing changed — only the backend knows whether it rolled back. A grant
        // that failed after the user writes began is partially applied, and saying otherwise
        // sends the employee away believing the company is untouched.
        setAlert(result.message || "The grant failed.", "warning", 15000);
        // Re-enable the button only when nothing was left behind: either a validation blocker
        // (no write was attempted, so `rolled_back` is absent) or a confirmed rollback. A
        // PARTIALLY APPLIED grant left an access record, so every retry would be refused as
        // "already has access" — the next step there is the company page, not this button.
        const safeToRetry = result.rolled_back !== false;
        setIsSubmitted(!safeToRetry);
        return;
      }
    } catch (err) {
      // The call itself failed (network, timeout, a thrown callable). We cannot know how far the
      // backend got, so do not assert anything about state.
      console.error(err);
      setAlert(
        `The grant failed and its outcome is unknown: ${
          err instanceof Error ? err.message : String(err)
        }. Check the company's access and logs before retrying.`,
        "warning",
        15000
      );
      setIsSubmitted(false);
      return;
    }

    // redirect back to company page
    navigate(`/company/${company.docId}`);
  };

  let subscriptionIds: string[], remainingProducts = []
  if (products) {
    subscriptionIds = access.map((sub) => sub.productId);
    remainingProducts = (products || []).filter(
      (product) => !subscriptionIds.includes(product.docId)
    );
  }

  return (
    <div className="m0 listings-wrapper middle half-width">
    <Breadcrumbs
      style={{ marginTop: "1rem" }}
      pathways={[
        ["Companies", "/companies"],
        ["Company", `/company/${params.companyId}`],
        ["New Access"],
      ]}
    />
    <PageHeader
      small={"New Access"}
      big={company ? company.companyName : ""}
      tiny={params.companyId}
    />
    {products === undefined ? (
      <LoadingCircle />
    ) : remainingProducts.length < 1 ? (
      <div className="alert alert-info" style={{ marginTop: "1rem" }}>
        This company already has access to all products! Click{" "}
        <button
          style={{
            backgroundColor: "transparent",
            border: "none",
            color: "teal",
            padding: 0,
            margin: 0,
          }}
          onClick={() => navigate(`companies/${params.companyId}`)}
        >
          <b>here</b>
        </button>{" "}
        to return.
      </div>
    ) : (
      <div>
        {/* date input */}
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <div style={{ marginTop: "1rem", width: "50%" }}>
            <DateInput
              inputBoxLabel="Expiration Date"
              name="expiration_date"
              onValidDateChange={(date) => setExpirationDate(date)}
              requiresFutureDate
            />
          </div>
        </div>

        {/* product dropdown */}
        <ProductsDropdown
          access={access.map((a) => a.productId)}
          products={products}
          onProductSelect={(p) => selectProduct(p)}
        />

        {/* TODO: add RLS to Product class and do a check here to see if RLS key
          * is present on productSelected before rendering RLSDimsSelector
          */}
        {(productSelected && productSelected.rls) ? (
          <RLSDimsSelector
            rlsValues={rlsValues}
            dimsSelected={rlsValuesSelected || []}
            updateRlsSelectedValues={setRlsValuesSelected}
          />
        ) : (
          <></>
        )}

        {/* users checkboxes selection */}
        {clients === undefined ? (
          <LoadingCircle />
        ) : (
          <UsersCheckboxes
            clients={clients}
            selectedUserIds={selectedUserIds || []}
            onSelectionChange={setSelectedUserIds}
          />
        )}

        {/* TODO:below is a flex box for no reason */}
        {/* submit/create access button */}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: "1rem",
            marginBottom: "3rem"
          }}
        >
          <Alert
            style={{ marginRight: "1rem" }}
            msg={alertState.msg}
            alertType={alertState.type}
          />
          <div style={{ marginLeft: "auto" }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={submit}
              title={"Create new access"}
              disabled={isSubmitted}
            >
              Create Access
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
  )
}

export default NewCompanySubscriptionPage;
export {}